import { createId } from '@paralleldrive/cuid2'
import { ROUTER_ORIGIN } from '../../analytics/pages'
import type { AnalyticsEvent } from '../../analytics/types'
import type { ApiOptions, Event, Features as AnalyticsFeatures } from './types'
import type { Features } from '../../config/features'
import { theWindow } from './helpers/env'
import { now } from './helpers/date'
import { extractTitle, extractCanonical } from './helpers/dom'
import { extractScreen, extractNetwork, extractTiming } from './helpers/window'
import { send } from './api'
import { getRequestURL, getRequestIP } from 'h3'

export type Track = (event: AnalyticsEvent) => Promise<boolean>

export function createTrack(apiOptions: ApiOptions) {
  return async (event: AnalyticsEvent) => {
    const evt = processEvent(event, apiOptions)
    return send(evt, apiOptions.server)
  }
}

export function processEvent(event: AnalyticsEvent, apiOptions: ApiOptions): Event {
  const aWindow = theWindow || ({ navigator: {}, document: {} } as unknown as Window)
  const nav = aWindow.navigator
  const doc = aWindow.document
  const route = useRoute()
  const {
    $cookie,
    $features,
    $pushNotificationsAllowed,
    $pushNotificationsSubscriptionId,
    $playInstallReferrer,
  } = useNuxtApp()
  const cookie = $cookie.value
  const userInfo = useUserInfo()

  // @ts-expect-error legacy types
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const tags = event.object.properties?.tags
  // @ts-expect-error legacy types
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (event.object.properties?.tags) event.object.properties.tags = undefined

  const reqEvent = import.meta.client ? undefined : useRequestEvent()
  const headers = reqEvent?.node.req.headers || {}

  const fullUrl = import.meta.client ? doc.URL : reqEvent ? getRequestURL(reqEvent).toString() : ''

  return {
    name: `${event.object.name} - ${event.action.name}`,
    messageId: createId(),
    userId: cookie.getId() || 'anonymousId',
    timestamp: now(),
    origin: event.origin ?? '',
    event,
    context: {
      app: apiOptions.clientName,
      appVersion: apiOptions.clientVersion,
      device: apiOptions.device,
      osVersion: apiOptions.osVersion,
      isMobileApp: __IS_MOBILE_APP__,
      library: 'analytics-module',
      page: {
        url: fullUrl,
        title: import.meta.client ? extractTitle() : '',
        routeName: String(route.name),
        canonicalUrl: import.meta.client ? extractCanonical() : '',
        referrer: import.meta.client ? doc.referrer : headers.referer || '',
        tags,
        // @ts-expect-error legacy types
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        category: event.object.properties?.pageCategory || route.meta?.pageType,
      },
      userAgent: import.meta.client ? nav.userAgent : headers['user-agent'] || '',
      geoLoc: cookie.getGeoLoc(),
      lang: apiOptions.lang || cookie.getLang() || 'en',
      languageTag: import.meta.client
        ? apiOptions.languageTag
        : headers['accept-language']?.split(',')[0] || 'N/A',
      abGroup: cookie.getAbGroup() || '',
      featureFlags: computeFeatureFlags($features?.getAllFeatures()),
      cookieEnabled: nav.cookieEnabled,
      localStorageEnabled: import.meta.client ? typeof aWindow.localStorage !== 'undefined' : false,
      pushNotificationsEnabled: __IS_MOBILE_APP__
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          $pushNotificationsAllowed?.value
        : undefined,
      pushNotificationSubscriptionId: __IS_MOBILE_APP__
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          $pushNotificationsSubscriptionId?.value
        : undefined,
      marketing: cookie.getMarketing(),
      screen: import.meta.client ? extractScreen(aWindow) : undefined,
      networkInformation: import.meta.client ? extractNetwork(aWindow) : undefined,
      performanceInformation: (event.origin ?? '') === ROUTER_ORIGIN && extractTiming(aWindow),
      appsflyerAttribution: cookie.getCookieValue('appsflyer'),
      countryLivingIn: __IS_MOBILE_APP__ ? userInfo.value.countryLivingIn : undefined,
      countryOrigin: __IS_MOBILE_APP__ ? (userInfo.value.countryOrigin as string) : undefined,
      countryLivingInDuration:
        __IS_MOBILE_APP__ && userInfo.value.overview !== 'planning_to_move'
          ? (userInfo.value.duration as string)
          : undefined,
      planningDate:
        __IS_MOBILE_APP__ && userInfo.value.overview === 'planning_to_move'
          ? (userInfo.value.duration as string)
          : undefined,
      situation: __IS_MOBILE_APP__ ? (userInfo.value.userSituation as string) : undefined,
      interests: __IS_MOBILE_APP__ ? (userInfo.value.interestOptions as string[]) : undefined,
      storeListingReferrerUrl: __IS_MOBILE_APP__
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          $playInstallReferrer?.value?.referrerUrl
        : undefined,
      'source-ip':
        import.meta.server && reqEvent
          ? getRequestIP(reqEvent, { xForwardedFor: true })
          : undefined,
    },
  }
}

function getFlagValue(value: unknown): string {
  if (value !== null && typeof value === 'object') {
    return Object.keys(value).length === 0 ? 'off' : 'on'
  }
  return String(value)
}

function computeFeatureFlags(
  features: Array<{ key: keyof Features; value: Features[keyof Features] | null }> | undefined,
): AnalyticsFeatures {
  const initial: { active: string[]; available: string[] } = { active: [], available: [] }
  const featureFlags = (features ?? []).reduce((features, f) => {
    features.available.push(f.key)
    features.active.push(`${f.key}:${getFlagValue(f.value)}`)
    return {
      ...features,
      [`experiment_${f.key}`]: getFlagValue(f.value),
    }
  }, initial)
  return featureFlags
}
